import React from 'react';
import { useTranslation } from 'react-i18next';
import type { InboundEmail, ContactFormSubmission } from '../../services/emailHubService';
import IntentBadge from './IntentBadge';

// Union type so the list can render either emails or contact forms
export type EmailListItem = InboundEmail | ContactFormSubmission;

function isInboundEmail(item: EmailListItem): item is InboundEmail {
  return 'fromAddress' in item;
}

function getSenderEmail(item: EmailListItem): string {
  return isInboundEmail(item) ? item.fromAddress : item.email;
}

function getSenderName(item: EmailListItem): string | undefined {
  return isInboundEmail(item) ? item.fromName : item.name;
}

function getSubject(item: EmailListItem): string {
  if (isInboundEmail(item)) return item.subject;
  return item.subject ?? item.message.slice(0, 60);
}

function getPreview(item: EmailListItem): string {
  if (isInboundEmail(item)) {
    return item.textBody?.slice(0, 100) ?? '';
  }
  return item.message.slice(0, 100);
}

function isUnread(item: EmailListItem): boolean {
  return item.status === 'new';
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `${days}d`;
}

interface EmailListProps {
  items: EmailListItem[];
  onSelect: (item: EmailListItem) => void;
  selectedId?: string | null;
  loading?: boolean;
  emptyMessage?: string;
}

const EmailList: React.FC<EmailListProps> = ({
  items,
  onSelect,
  selectedId,
  loading,
  emptyMessage,
}) => {
  const { t } = useTranslation('emailHub');

  if (loading) {
    return (
      <div className="email-hub-list email-hub-list--loading">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="email-hub-list-skeleton" />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="email-hub-list email-hub-list--empty">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
        <p>{emptyMessage ?? t('list.empty', 'No messages found')}</p>
      </div>
    );
  }

  return (
    <div className="email-hub-list">
      {items.map((item) => {
        const senderEmail = getSenderEmail(item);
        const senderName = getSenderName(item);
        const displayName = senderName || senderEmail;
        const initial = displayName.charAt(0).toUpperCase();
        const unread = isUnread(item);
        const selected = item.id === selectedId;

        return (
          <button
            key={item.id}
            className={`email-hub-list-item ${selected ? 'email-hub-list-item--selected' : ''} ${unread ? 'email-hub-list-item--unread' : ''}`}
            onClick={() => onSelect(item)}
          >
            {/* Avatar */}
            <div className="email-hub-list-item__avatar" aria-hidden="true">
              {initial}
            </div>

            {/* Content */}
            <div className="email-hub-list-item__body">
              <div className="email-hub-list-item__header">
                <span className="email-hub-list-item__sender">{displayName}</span>
                <span className="email-hub-list-item__time">{timeAgo(item.createdAt)}</span>
              </div>
              <div className="email-hub-list-item__subject">
                {unread && <span className="email-hub-list-item__dot" aria-label={t('list.unread', 'Unread')} />}
                {getSubject(item)}
              </div>
              <div className="email-hub-list-item__footer">
                <span className="email-hub-list-item__preview">{getPreview(item)}</span>
                <div className="email-hub-list-item__badges">
                  {item.intent && (
                    <IntentBadge intent={item.intent} confidence={item.intentConfidence} size="sm" />
                  )}
                  {isInboundEmail(item) && (
                    <span className={`email-hub-priority-badge email-hub-priority-badge--${item.priority}`}>
                      {t(`priority.${item.priority}`, item.priority)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default EmailList;
